import { Router } from 'next/router';
import { useEffect } from 'react';
import NProgress from 'nprogress';

import { useTheme } from '@mui/material';

import { ROUTE_PROGRESS_BAR_ID } from '@/shared/config/routeProgress';
import { doneRouteProgress, startRouteProgress } from '@/shared/lib/routeProgress';

const RouteProgressBar = () => {
  const theme = useTheme();

  useEffect(() => {
    try {
      NProgress.configure({ parent: `#${ROUTE_PROGRESS_BAR_ID}`, showSpinner: false });

      /**
       * Route 변경 시작 핸들러
       * - NProgress.isStarted()로 중복 실행 방지
       * - 서비스 내부 redirect로 인한 2번째 routeChangeStart 무시
       */
      const handleRouteStart = () => {
        startRouteProgress();
      };

      /**
       * Route 완료 핸들러 (공통)
       * - NProgress 종료 처리
       */
      const handleRouteEnd = () => {
        doneRouteProgress();
      };

      /**
       * Route 변경 완료 핸들러
       */
      const handleRouteComplete = () => {
        handleRouteEnd();
      };

      // Next.js Router 이벤트 등록
      const handleRouteChangeStart = (_url: string, { shallow }: { shallow: boolean }) => {
        if (shallow) return;
        handleRouteStart();
      };
      const handleRouteChangeComplete = (_url: string, { shallow }: { shallow: boolean }) => {
        if (shallow) return;
        handleRouteComplete();
      };
      const handleRouteChangeError = (
        _error: Error,
        _url: string,
        { shallow }: { shallow: boolean },
      ) => {
        if (shallow) return;
        handleRouteComplete();
      };

      Router.events.on('routeChangeStart', handleRouteChangeStart);
      Router.events.on('routeChangeComplete', handleRouteChangeComplete);
      Router.events.on('routeChangeError', handleRouteChangeError);

      return () => {
        Router.events.off('routeChangeStart', handleRouteChangeStart);
        Router.events.off('routeChangeComplete', handleRouteChangeComplete);
        Router.events.off('routeChangeError', handleRouteChangeError);
      };
    } catch (error) {
      console.error('Error setting up route progress bar:', error);
      return undefined;
    }
  }, []);

  return (
    <>
      <style jsx global>
        {`
          /* Make clicks pass-through */
          #nprogress {
            pointer-events: none;
          }

          #nprogress .bar {
            background: #3365e9;

            position: fixed;
            z-index: ${theme.zIndex.navigationTop};
            top: 0;
            left: 0;

            width: 100%;
            height: 4px;
          }

          /* Fancy blur effect */
          #nprogress .peg {
            display: block;
            position: absolute;
            right: 0px;
            width: 100px;
            height: 100%;
            box-shadow:
              0 0 10px #29d,
              0 0 5px #29d;
            opacity: 1;

            -webkit-transform: rotate(3deg) translate(0px, -4px);
            -ms-transform: rotate(3deg) translate(0px, -4px);
            transform: rotate(3deg) translate(0px, -4px);
          }

          /* Remove these to get rid of the spinner */
          #nprogress .spinner {
            display: block;
            position: fixed;
            z-index: ${theme.zIndex.navigationTop};
            top: 15px;
            right: 15px;
          }

          #nprogress .spinner-icon {
            width: 18px;
            height: 18px;
            box-sizing: border-box;

            border: solid 2px transparent;
            border-top-color: #29d;
            border-left-color: #29d;
            border-radius: 50%;

            -webkit-animation: nprogress-spinner 400ms linear infinite;
            animation: nprogress-spinner 400ms linear infinite;
          }

          .nprogress-custom-parent {
            overflow: hidden;
            position: relative;
          }

          .nprogress-custom-parent #nprogress .spinner,
          .nprogress-custom-parent #nprogress .bar {
            position: absolute;
          }

          @-webkit-keyframes nprogress-spinner {
            0% {
              -webkit-transform: rotate(0deg);
            }
            100% {
              -webkit-transform: rotate(360deg);
            }
          }
          @keyframes nprogress-spinner {
            0% {
              transform: rotate(0deg);
            }
            100% {
              transform: rotate(360deg);
            }
          }
        `}
      </style>
    </>
  );
};

export default RouteProgressBar;
